<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('order.hafciarnia.ordersInHafciarnia') }}</span>
        <h3 class="page-title">{{ $t('order.hafciarnia.listOfOordersInHafciarnia') }}</h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/zamowienia/nowe')">
          <i class="material-icons">add</i>
          {{ $t('order.addNewOrder') }}
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="allDataLoaded"
      :view-name="'orders-hafciarnia'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('ui.order') }}</th>
            <th
              scope="col"
              class="border-0 text-center">{{ $t('order.hafciarnia.dateOfTransferToHafciarnia') }}</th>
            <th
              scope="col"
              class="border-0 text-center">{{ $t('order.hafciarnia.syncDate') }}</th>
            <th
              scope="col"
              class="border-0 text-center">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr
            v-for="(order, index) of orders"
            :key="'order-' + index">
            <td class="align-middle">
              {{ order.order_name }}<br>
              <a
                v-if="order.embriodery_id"
                :href="hafciarniaUrl + 'zamowienia/edycja/' + parseInt(order.embriodery_id, 10)"
                target="_blank"
                rel="noopener noreferrer">
                {{ $t('order.hafciarnia.orderInHafciarnia') }}
              </a>
            </td>
            <td class="align-middle text-center">
              {{ order.embriodery_sent_at || '-' }}
            </td>
            <td class="align-middle text-center">
              <template v-if="!!order.embriodery_sent_at && !!order.embriodery_synchronized_at">
                <d-badge
                  theme="success"
                  :outline="true">
                  {{ $t('order.hafciarnia.synced') }}
                </d-badge><br>
                <small class="mt-2 d-block">{{ order.embriodery_synchronized_at }}</small>
              </template>
              <template v-else-if="!!order.embriodery_sent_at && !order.embriodery_synchronized_at">
                <d-badge
                  theme="warning"
                  :outline="true">
                  {{ $t('order.hafciarnia.syncInProgress') }}
                </d-badge>
              </template>
              <template v-else>
                <d-badge
                  theme="danger"
                  :outline="true">
                  {{ $t('order.hafciarnia.notSynced') }}
                </d-badge>
              </template>
            </td>
            <td
              class="align-middle text-center"
              style="width: 80px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/zamowienia-hafciarnia/edycja/' + order.id)"
                class="mx-1"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td
              colspan="5"
              class="text-center">
              {{ $t('purchase.noOrdersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import AppConfig from '@/.env';
import CustomerPanel from './../mixins/CustomerPanel.vue';
import ListUtils from '@/utils/ListUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import HasPagination from '@/mixins/HasPagination.vue';
import Pagination from '@/components/common/Pagination.vue';
import CalendarUtils from '@/utils/CalendarUtils.js';
import debounce from 'lodash.debounce';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'orders-hafciarnia',
  mixins: [
    CustomerPanel,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers && this.loadedProducts && this.loadedProductTags;
    },
    userIsWarehouseman () {
      return this.$store.getters.getUserType === 'warehouse_manager';
    },
    userIsCustomer () {
      return this.$store.getters.getUserType === 'customer';
    },
    hafciarniaUrl () {
      let environment = AppConfig.useConfig;
      return AppConfig.additionalConfig[environment].hafciarniaUrl;
    },
    preloadedProductCodesOptions () {
      let options = [];
      for (let i = 0; i < this.products.length; i++) {
        options.push({
          text: this.products[i].code,
          value: '[#' + this.products[i].id + '#]' + this.products[i].code
        });
      }

      return options;
    },
    orderWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    orderStatusOptions () {
      return [
        {
          text: this.$t('order.toCollect'),
          value: 'TO_COLLECT'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PURCHASE_PRODUCTS'
        },
        {
          text: this.$t('ui.order'),
          value: 'ORDER'
        },
        {
          text: this.$t('order.inProduction'),
          value: 'IN_PRODUCTION'
        },
        {
          text: this.$t('order.toCheckInWarehouse'),
          value: 'TO_CHECK'
        },
        {
          text: this.$t('order.readyForShipping'),
          value: 'PREPARATION_FOR_SHIPPING'
        },
        {
          text: this.$t('order.waitingForShipping'),
          value: 'WAITING_FOR_SHIPPING'
        },
        {
          text: this.$t('order.sent'),
          value: 'SENT'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'PENDING_PAYMENT'
        },
        {
          text: this.$t('order.ended'),
          value: 'ENDED'
        },
        {
          text: this.$t('order.toDelete'),
          value: 'TO_DELETE'
        },
        {
          text: this.$t('order.deleted'),
          value: 'DELETED'
        }
      ];
    },
    productTagsOptions () {
      let options = [];

      for (let i = 0; i < this.productTags.length; i++) {
        for (let j = 0; j < this.productTags[i].tags.length; j++) {
          let tagName = this.productTags[i].tags[j].name;
          let tagID = this.productTags[i].tags[j].id;
          let tagCategoryID = this.productTags[i].tags[j].category_id;
          let tagCategoryName = this.productTags[i].name;

          options.push({
            text: tagCategoryName + ': ' + tagName,
            value: tagCategoryID + ';' + tagID
          });
        }
      }

      return options;
    },
    filtersConfig () {
      return [
        {
          key: 'order_name',
          apiKey: 'order_name',
          label: this.$t('ui.orderName'),
          placeholder: this.$t('ui.orderName'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          maxWidth: '25%'
        },
        {
          key: 'created_at_range',
          label: this.$t('ui.chooseDate'),
          type: 'daterange',
          isBasic: true,
          defaultValue: { startDate: null, endDate: null },
          maxWidth: '300px',
          apiTransform: (val, where) => {
            if (val.startDate) {
              where['created_at_from'] = CalendarUtils.formatDateForPHP(new Date(val.startDate));
            }
            if (val.endDate) {
              where['created_at_to'] = CalendarUtils.formatDateForPHP(new Date(val.endDate));
            }
          }
        },
        {
          key: 'status',
          apiKey: 'status',
          label: this.$t('status.statusQuestionMark'),
          placeholder: this.$t('status.statusQuestionMark'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.orderStatusOptions,
          maxWidth: '25%',
          permission: () => !this.userIsWarehouseman
        },
        {
          key: 'customer_id',
          apiKey: 'customer_id',
          label: this.$t('users.customer'),
          type: 'customerSelect',
          isBasic: true,
          defaultValue: null,
          maxWidth: '25%',
          permission: () => !this.userIsWarehouseman && !this.userIsCustomer
        },
        {
          key: 'user_id',
          apiKey: 'user_id',
          label: this.$t('order.isWorker'),
          placeholder: this.$t('order.isWorker'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderWorkerOptions,
          permission: () => !this.userIsWarehouseman && !this.userIsCustomer
        },
        {
          key: 'productCode',
          label: this.$t('product.productsCodes'),
          placeholder: this.$t('product.productsCodes'),
          type: 'select',
          multiple: true,
          isBasic: false,
          defaultValue: [],
          options: this.preloadedProductCodesOptions,
          apiTransform: (val, where) => {
            if (!where['products']) {
              where.products = {};
            }
            where['products']['code'] = val.map(v => v.replace(/\[\#[0-9]{1,}\#\]/gmi, ''));
          }
        },
        {
          key: 'productTags',
          label: this.$t('order.productsTags'),
          type: 'select',
          multiple: true,
          isBasic: false,
          defaultValue: [],
          options: this.productTagsOptions,
          apiTransform: (val, where) => {
            if (!where['products']) {
              where.products = {};
            }
            let output = {};
            val.forEach(tagDataStr => {
              let tagData = tagDataStr.split(';');
              let catId = parseInt(tagData[0], 10);
              let tagId = parseInt(tagData[1], 10);
              if (!output[catId]) {
                output[catId] = [];
              }
              output[catId].push(tagId);
            });
            where['products']['tags'] = output;
          }
        }
      ];
    }
  },
  data () {
    return {
      orders: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      workers: [],
      loadedWorkers: false,
      products: [],
      loadedProducts: false,
      loadedProductTags: false,
      productTags: [],
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      where.in_embriodery = 1;

      if (!where.status) {
        where.excluded_status = ['INQUIRY', 'NEW', 'SENT_TO_CUSTOMER'];
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/orders/items?page=' + this.currentPage,
        listField: 'orders',
        method: 'get',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      if (!this.userIsCustomer) {
        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/users/items'),
          loadedKey: 'loadedWorkers',
          params: {
            return: 'get',
            where: {
              type: ['worker', 'admin']
            }
          },
          outputKey: 'workers',
          errorKey: 'loadError'
        });

        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/tags/categories/items'),
          loadedKey: 'loadedProductTags',
          params: {
            with: ['tags']
          },
          outputKey: 'productTags',
          errorKey: 'loadError'
        });
      }

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/products/all-items-id-code'),
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });
    }
  }
};
</script>
